import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import { baseUrl } from "./axiosInstance";

interface TokenData {
    access: string;
    refresh: string;
}

interface RefreshTokenResponse {
    access: string;
    refresh: string;
}

interface QueueItem {
    resolve: (token: string) => void;
    reject: (error: unknown) => void;
}

interface CustomOptions {
    attachTokenToRequest?: (request: AxiosRequestConfig, token: string) => void;
    handleTokenRefresh?: () => Promise<RefreshTokenResponse>;
    setTokenData?: (tokenData: TokenData, axiosClient: AxiosInstance) => void;
    shouldIntercept?: (error: AxiosError) => boolean;
}

const shouldIntercept = (error: AxiosError): boolean => {
    try {
        return error.response?.status === 401;
    } catch (e) {
        return false;
    }
};

const setTokenData = (
    tokenData: TokenData = {} as TokenData,
    _axiosClient: AxiosInstance
): void => {
    localStorage.setItem("authTokens", JSON.stringify(tokenData));
};

export const handleTokenRefresh = (): Promise<RefreshTokenResponse> => {
    localStorage.setItem("isTokenExpired", "true");
    try {
        const authTokens = window.localStorage.getItem("authTokens");
        const isInternal = window.localStorage.getItem("is_internal");

        if (!authTokens) {
            throw new Error("No auth tokens found");
        }

        const { refresh }: TokenData = JSON.parse(authTokens) as TokenData;
        const is_internal = isInternal
            ? (JSON.parse(isInternal) as boolean)
            : false;

        return new Promise<RefreshTokenResponse>((resolve, reject) => {
            axios
                .post<RefreshTokenResponse>(
                    `${baseUrl}/person/token/refresh/`,
                    {
                        refresh,
                        ...(is_internal ? { is_internal: true } : {}),
                    }
                )
                .then(({ data }) => {
                    localStorage.setItem("isTokenExpired", "false");
                    setTokenData(data, axios);
                    resolve(data);
                })
                .catch((err) => {
                    window.localStorage.removeItem("authTokens");
                    window.localStorage.removeItem("is_internal");
                    window.location.reload();
                    reject(err);
                });
        });
    } catch (err) {
        console.error(err);
        window.localStorage.removeItem("authTokens");
        window.localStorage.removeItem("is_internal");
        window.location.reload();
        return Promise.reject(err);
    }
};

const attachTokenToRequest = (
    request: AxiosRequestConfig,
    token: string
): void => {
    if (!request.headers) {
        request.headers = {};
    }
    request.headers["Authorization"] = "Bearer " + token;

    // If there is an edge case where access token is also set in request query,
    // this is also a nice place to add it
    // Example: /orders?token=xyz-old-token
    if (request.url && /\/orders/.test(request.url)) {
        if (!request.params) {
            request.params = {};
        }
        request.params.token = token;
    }
};

const applyAppTokenRefreshInterceptor = (
    axiosClient: AxiosInstance,
    customOptions: CustomOptions = {}
): void => {
    let isRefreshing = false;
    let failedQueue: QueueItem[] = [];

    const options: Required<CustomOptions> = {
        attachTokenToRequest,
        handleTokenRefresh,
        setTokenData,
        shouldIntercept,
        ...customOptions,
    };

    const processQueue = (
        error: unknown,
        token: string | null = null
    ): void => {
        failedQueue.forEach((prom) => {
            if (error) {
                prom.reject(error);
            } else {
                prom.resolve(token!);
            }
        });

        failedQueue = [];
    };

    const interceptor = (error: AxiosError): Promise<unknown> => {
        if (!options.shouldIntercept(error)) {
            return Promise.reject(error);
        }

        const config = error.config as AxiosRequestConfig & {
            _retry?: boolean;
            _queued?: boolean;
        };
        if (config._retry || config._queued) {
            return Promise.reject(error);
        }

        const originalRequest = config;
        if (isRefreshing) {
            return new Promise<string>((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            })
                .then((token: string) => {
                    originalRequest._queued = true;
                    options.attachTokenToRequest(originalRequest, token);
                    return axiosClient.request(originalRequest);
                })
                .catch((_err) => {
                    return Promise.reject(error); // Ignore refresh token request's "err" and return actual "error" for the original request
                });
        }

        originalRequest._retry = true;
        isRefreshing = true;
        return new Promise((resolve, reject) => {
            options
                .handleTokenRefresh()
                .then((tokenData: RefreshTokenResponse) => {
                    options.setTokenData(tokenData, axiosClient);
                    options.attachTokenToRequest(
                        originalRequest,
                        tokenData.access
                    );
                    processQueue(null, tokenData.access);
                    resolve(axiosClient.request(originalRequest));
                })
                .catch((err) => {
                    processQueue(err, null);
                    reject(err);
                })
                .finally(() => {
                    isRefreshing = false;
                });
        });
    };

    axiosClient.interceptors.response.use(undefined, interceptor);
};

export default applyAppTokenRefreshInterceptor;
